import { useMemo, createContext, useContext } from 'react';
import { useIntl } from 'react-intl';
import { CustomerDataWithStatusLoginStatusEnum } from '@empiriecom/mybuy-frontend-api/backend-mybuy-customer/v1/models/CustomerDataWithStatus';
import type { CustomerDataWithStatus } from '@empiriecom/mybuy-frontend-api/backend-mybuy-customer/v1/models/CustomerDataWithStatus';
import type { CustomerStatus } from '@empiriecom/mybuy-frontend-api/backend-mybuy-customer/v1/models/CustomerStatus';
import useSWR from 'swr';
import type { CustomerInformation } from '@empiriecom/mybuy-frontend-api/backend-mybuy-customer/v1/models/CustomerInformation';
import type {
  AbstractCustomerData,
  CustomerApi,
} from '@empiriecom/mybuy-frontend-api/backend-mybuy-customer/v1';
import { useCustomerApi } from './useCustomerApi';
import { useSession } from '../SessionProvider';

interface CustomerProviderProps {
  children: React.ReactNode;
}

interface CustomerContextProps {
  isMyAccountAccessDenied: boolean;
  isLoading: boolean;
  isLoggedIn: boolean;
  customer?: CustomerDataWithStatus;
  customerInformation?: CustomerInformation;
  token?: string;
  customerStatus?: CustomerStatus;
  abstractCustomer?: AbstractCustomerData;
}

const defaultCustomerContext = (): CustomerContextProps => ({
  isMyAccountAccessDenied: false,
  isLoading: false,
  isLoggedIn: false,
  customer: undefined,
  customerInformation: undefined,
  customerStatus: undefined,
  abstractCustomer: undefined,
});

export const CustomerContext = createContext<CustomerContextProps>(defaultCustomerContext());
export const useCustomer = (): CustomerContextProps => useContext(CustomerContext);

const customerFetcher = (customerApi: CustomerApi, locale: string) => async () => {
  const [customerResult, customerStatusResult, customerInformationResult] = await Promise.all([
    customerApi.getCustomer({ ecLocale: locale }),
    customerApi.getCustomerStatus({
      ecLocale: locale,
      force: true,
    }),
    customerApi.getInformation(),
  ]);
  return {
    customerResult,
    customerStatusResult,
    customerInformationResult,
  };
};

const softLoginFetcher = (customerApi: CustomerApi, locale: string) => async () => {
  const [abstractCustomer, customerInformationResult] = await Promise.all([
    customerApi.getCustomerAbstract({ ecLocale: locale }),
    customerApi.getInformation(),
  ]);
  return {
    abstractCustomer,
    customerInformationResult,
  };
};

export const CustomerProvider = ({ children }: CustomerProviderProps): JSX.Element => {
  const customerApi = useCustomerApi();
  const { softLoggedIn } = useSession();

  const { locale } = useIntl();

  // TODO: replace complete provider and context with SWR functionality
  const { data, isLoading } = useSWR(
    customerApi && !softLoggedIn ? 'customer' : null,
    customerFetcher(customerApi!, locale),
  );

  const { data: softLoginData, isLoading: isLoadingAbstract } = useSWR(
    customerApi && softLoggedIn ? 'abstractCustomer' : null,
    softLoginFetcher(customerApi!, locale),
  );

  const {
    abstractCustomer,
    customerResult: customer,
    customerStatusResult: customerStatus,
    customerInformationResult: customerInformation,
  } = { ...softLoginData, ...data };

  const customerLoginStatus = customer?.loginStatus;
  const isLoggedIn =
    customerLoginStatus === CustomerDataWithStatusLoginStatusEnum.LoggedIn ||
    customerLoginStatus === CustomerDataWithStatusLoginStatusEnum.ForeignerLoggedIn;

  const value = useMemo(
    () => ({
      isMyAccountAccessDenied:
        isLoggedIn &&
        customerLoginStatus === CustomerDataWithStatusLoginStatusEnum.ForeignerLoggedIn,
      isLoading: isLoading || isLoadingAbstract,
      isLoggedIn,
      customer,
      customerInformation,
      customerStatus,
      abstractCustomer,
    }),
    [
      isLoggedIn,
      abstractCustomer,
      customer,
      customerInformation,
      customerLoginStatus,
      customerStatus,
      isLoading,
      isLoadingAbstract,
    ],
  );

  return <CustomerContext.Provider value={value}>{children}</CustomerContext.Provider>;
};
